import { AlertTriangle, ArrowDownRight, ArrowUpRight, Minus, Sparkles } from 'lucide-react';
import {
  buildOverviewHighlights,
  buildSocialOverviewView,
  type OverviewHighlight,
} from '../../../lib/reporting/socialOverview';
import type { SocialOverview } from '../../../types/socialReporting';
import { cn } from '../../../lib/utils';
import { SocialKpiCard } from './SocialKpiCard';
import { SocialTrendExplorer } from './SocialTrendExplorer';
import { PlatformMiniCard } from './PlatformMiniCard';
import { BestPostCard } from './BestPostCard';

// Organic Social Reporting — Overview tab (the executive scorecard). All layout
// is one unified template: a Mazda (GBP-only) report and a Snap (FB+IG) report
// render identically; the GBP/FB-story gaps show as graceful "not measurable"
// states inside the same cards, never an industry branch.
export function SocialOverviewTab({ data }: { data: SocialOverview }) {
  const view = buildSocialOverviewView(data);
  const highlights = buildOverviewHighlights(data);

  return (
    <div className="space-y-6">
      {view.failures.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">Some data couldn't be loaded.</p>
            <ul className="mt-0.5 list-inside list-disc text-xs text-amber-700">
              {view.failures.map((f) => (
                <li key={f.source}>
                  {f.source}: {f.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Hero scorecard — 6 KPIs, each with a period-over-period delta. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {view.heroCards.map((card) => (
          <SocialKpiCard key={card.key} card={card} />
        ))}
      </div>

      {/* "What happened this period" — real, data-driven highlights (the biggest
          movers across all platforms incl. GBP + reviews). The AI-narrative home later. */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-kyck-600" aria-hidden />
          <h3 className="text-sm font-medium text-slate-700">What happened this period</h3>
        </div>
        {highlights.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {highlights.map((h) => (
              <HighlightChip key={h.key} highlight={h} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">
            No standout movement this period. This client's story may be in reviews and community
            response — see the Community &amp; Reputation tab.
          </p>
        )}
      </div>

      {/* Multi-metric × multi-platform trend explorer. */}
      <SocialTrendExplorer data={data} />

      {/* Per-platform mini-cards, each with that channel's best post beneath it.
          FB/IG only carry a best post — GBP keeps its "not measurable" state with
          no best-post block (no post analytics = no ranking to make). */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {view.platformCards.map((card) => {
          const best = data.bestPosts.find((b) => b.platform === card.platform) ?? null;
          return (
            <div key={card.platform} className="space-y-3">
              <PlatformMiniCard card={card} />
              {best && <BestPostCard post={best} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// One highlight as a chip: metric label · change phrase, tinted by direction.
function HighlightChip({ highlight }: { highlight: OverviewHighlight }) {
  const { direction } = highlight;
  const Icon = direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : Minus;
  const tone =
    direction === 'up'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : direction === 'down'
        ? 'border-red-200 bg-red-50 text-red-800'
        : 'border-slate-200 bg-slate-50 text-slate-700';
  return (
    <li
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm',
        tone,
      )}
    >
      {direction && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      <span className="font-medium">{highlight.label}</span>
      <span className="opacity-80">{highlight.detail}</span>
    </li>
  );
}
